import type {
  AgentWorkbenchPatch,
  AgentWorkbenchSectionContent,
  AgentWorkSurfaceManifest,
  AgentWorkSurfaceSection,
} from "../../../../shared/types";
import { Card } from "../ui/Card.tsx";
import { Button } from "../ui/Button.tsx";

export interface PreviewDiffRow {
  sectionId: string;
  label: string;
  before: AgentWorkbenchSectionContent;
  after: AgentWorkbenchSectionContent;
}

export function WorkbenchPatchPreview({
  manifest,
  patch,
  onApply,
  onAskAgain,
  onReject,
  onCancel,
  applying = false,
  retrying = false,
}: {
  manifest: AgentWorkSurfaceManifest;
  patch: AgentWorkbenchPatch;
  onApply?: () => void;
  onAskAgain?: () => void;
  onReject?: () => void;
  onCancel?: () => void;
  applying?: boolean;
  retrying?: boolean;
}) {
  const diffs = buildPreviewDiffs(manifest, patch);

  return (
    <Card className="rounded-xl border-brand-200 bg-brand-50 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-700">
            Proposed Patch
          </p>
          <h3 className="mt-1 text-base font-semibold text-slate-900">Review before apply</h3>
          <p className="mt-2 text-sm text-slate-700">{patch.rationale}</p>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        {diffs.map((diff) => (
          <div key={diff.sectionId} className="rounded-xl border border-brand-100 bg-white p-3">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-slate-900">{diff.label}</p>
              <span className="rounded-full bg-brand-50 px-2 py-1 text-xs font-semibold text-brand-700">
                {diff.sectionId}
              </span>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Before</p>
                <div className="min-h-[72px] rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                  {renderContent(diff.before)}
                </div>
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">After</p>
                <div className="min-h-[72px] rounded-lg border border-brand-200 bg-brand-50/60 p-3 text-sm text-slate-900">
                  {renderContent(diff.after)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} disabled={applying || retrying}>Cancel</Button>
        <Button variant="secondary" onClick={onReject} disabled={applying || retrying}>Reject</Button>
        <Button variant="secondary" onClick={onAskAgain} disabled={applying || retrying} loading={retrying}>
          Ask again
        </Button>
        <Button variant="primary" onClick={onApply} disabled={applying || retrying}>
          {applying ? "Applying..." : "Apply patch"}
        </Button>
      </div>
    </Card>
  );
}

export function buildPreviewDiffs(
  manifest: AgentWorkSurfaceManifest,
  patch: AgentWorkbenchPatch,
): PreviewDiffRow[] {
  const touchedSectionIds = [...new Set(patch.operations.map((operation) => operation.section_id))];
  return touchedSectionIds.map((sectionId) => {
    const section = manifest.sections.find((candidate) => candidate.id === sectionId);
    if (!section) {
      return {
        sectionId,
        label: sectionId,
        before: { kind: "text", text: "" },
        after: { kind: "text", text: "" },
      };
    }

    const after = applyOperationsToSection(
      section,
      patch.operations.filter((operation) => operation.section_id === sectionId),
    );

    return {
      sectionId,
      label: section.label,
      before: section.content,
      after,
    };
  });
}

function applyOperationsToSection(
  section: AgentWorkSurfaceSection,
  operations: AgentWorkbenchPatch["operations"],
): AgentWorkbenchSectionContent {
  let next: AgentWorkbenchSectionContent = cloneContent(section.content);

  for (const operation of operations) {
    switch (operation.op) {
      case "replace_field":
        if (next.kind === "text" && operation.path === "content.text") {
          next = { kind: "text", text: operation.value };
        }
        if (next.kind === "markdown" && operation.path === "content.markdown") {
          next = { kind: "markdown", markdown: operation.value };
        }
        break;
      case "rewrite_text":
        if (next.kind === "text" && operation.path === "content.text") {
          next = { kind: "text", text: operation.value };
        }
        if (next.kind === "markdown" && operation.path === "content.markdown") {
          next = { kind: "markdown", markdown: operation.value };
        }
        break;
      case "append_list_item":
        if (next.kind === "string_list") {
          next = { kind: "string_list", items: [...next.items, operation.value] };
        }
        break;
      case "remove_list_item":
        if (next.kind === "string_list") {
          next = { kind: "string_list", items: next.items.filter((item) => item !== operation.value) };
        }
        break;
      case "create_variant":
        next = operation.value;
        break;
    }
  }

  return next;
}

function cloneContent(content: AgentWorkbenchSectionContent): AgentWorkbenchSectionContent {
  switch (content.kind) {
    case "text":
      return { ...content };
    case "markdown":
      return { ...content };
    case "string_list":
      return { kind: "string_list", items: [...content.items] };
    case "labeled_fields":
      return { kind: "labeled_fields", fields: content.fields.map((field) => ({ ...field })) };
    case "json":
      return { kind: "json", value: structuredClone(content.value) };
  }
}

function renderContent(content: AgentWorkbenchSectionContent) {
  switch (content.kind) {
    case "text":
      return <p className="whitespace-pre-wrap leading-6">{content.text}</p>;
    case "markdown":
      return <div className="whitespace-pre-wrap leading-6">{content.markdown}</div>;
    case "string_list":
      return (
        <ul className="space-y-2">
          {content.items.map((item, index) => <li key={`${item}-${index}`}>• {item}</li>)}
        </ul>
      );
    case "labeled_fields":
      return (
        <dl className="space-y-2">
          {content.fields.map((field) => (
            <div key={field.key}>
              <dt className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{field.label}</dt>
              <dd className="mt-1">{field.value}</dd>
            </div>
          ))}
        </dl>
      );
    case "json":
      return <pre className="overflow-x-auto text-xs">{JSON.stringify(content.value, null, 2)}</pre>;
  }
}
